import { useMedplum } from '@medplum/react-hooks';
import type { Address, Attachment, CodeableConcept, ContactPoint, DocumentReference, HumanName, Identifier, Practitioner } from '@medplum/fhirtypes';
import { Group, PasswordInput, Select, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { UnifyDateInput } from '@/components/UnifyDateInput';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { IconCalendarEvent } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BIO_EXT,
  CASELOAD_PREFERENCE_EXT,
  NPI_IDENTIFIER_SYSTEM,
  PROVIDER_DOCUMENT_TYPE_SYSTEM,
  SSN_IDENTIFIER_SYSTEM,
} from '@unify/fhir-model/constants';
import { getBio, getCaseloadPreference, getNuccTaxonomyCode, resolvePractitionerLegalName, resolvePractitionerPreferredNamesForForm, setBio, setCaseloadPreference, stripPractitionerLegacyPreferredNames, upsertNuccTaxonomyQualification, upsertPractitionerNickname, upsertPractitionerOfficialName } from '@unify/fhir-model/resources/practitioner';
import {
  personalInfoFieldLabels,
  personalInfoSaveSchema,
  personalInfoSchema,
  type PersonalInfoFormValues,
} from '@/schemas/practitioner.schema';
import { ProfileCheckboxSection } from '../../../components/ProfileCheckboxSection';
import { DropzoneField } from '../../../components/DropzoneField';
import { OnboardingSection } from '../OnboardingSection';
import { LANGUAGES_OPTIONS, CASELOAD_PREFERENCE_OPTIONS, NUCC_TAXONOMY_OPTIONS, US_STATES } from '../constants';
import { useOnboardingPractitioner } from '../useOnboardingPractitioner';
import { completeProviderOnboardingSection } from '../../../lib/providerOnboardingService';
import { practitionerPreferredReference } from '../../../lib/loadPractitionerPreferredLabels';
import { digitsOnly, missingFieldMessage } from '../../../lib/formValidation';
import { createSecuredAttachment } from '../../../lib/createSecuredAttachment';

function getNameByUse(resource: Practitioner, use: HumanName['use']): HumanName | undefined {
  return resource.name?.find((name) => name.use === use);
}

function getIdentifierValue(resource: Practitioner, system: string): string {
  return resource.identifier?.find((identifier) => identifier.system === system)?.value ?? '';
}

function removeExtension(resource: Practitioner, url: string): Practitioner {
  return {
    ...resource,
    extension: (resource.extension ?? []).filter((extension) => extension.url !== url),
  };
}

function upsertName(resource: Practitioner, use: HumanName['use'], buildName?: HumanName): Practitioner {
  const names = (resource.name ?? []).filter((name) => name.use !== use);
  if (!buildName) {
    return { ...resource, name: names };
  }

  return { ...resource, name: [...names, buildName] };
}

function upsertTelecom(resource: Practitioner, point: ContactPoint): Practitioner {
  const telecom = (resource.telecom ?? []).filter(
    (entry) => !(entry.system === point.system && entry.use === point.use),
  );

  if (!point.value?.trim()) {
    return { ...resource, telecom };
  }

  return {
    ...resource,
    telecom: [...telecom, { ...point, value: point.value.trim() }],
  };
}

function upsertIdentifier(resource: Practitioner, nextIdentifier: Identifier): Practitioner {
  const identifiers = (resource.identifier ?? []).filter((identifier) => identifier.system !== nextIdentifier.system);
  return {
    ...resource,
    identifier: [...identifiers, nextIdentifier],
  };
}

function buildLanguageConcepts(languages: string[]): CodeableConcept[] {
  return languages.map((language) => ({ text: language }));
}

function buildHeadshotAttachment(file: File, attachment: Attachment): Attachment {
  return {
    ...attachment,
    title: attachment.title ?? file.name,
    contentType: attachment.contentType ?? file.type,
  };
}

function formatPhoneNumber(value: string): string {
  const digits = digitsOnly(value).slice(0, 10);
  if (digits.length === 0) {
    return '';
  }
  if (digits.length < 4) {
    return `(${digits}`;
  }
  if (digits.length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function PersonalInfoSection(): JSX.Element {
  const medplum = useMedplum();
  const profile = useOnboardingPractitioner();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const hydratedProfileVersionRef = useRef<string | undefined>(undefined);
  const form = useForm<PersonalInfoFormValues>({
    initialValues: {
      businessPhone: '',
      firstName: '',
      lastName: '',
      preferredFirstName: '',
      preferredLastName: '',
      aliasFirst: '',
      aliasMiddle: '',
      aliasLast: '',
      npi: '',
      nuccTaxonomyCode: null,
      ssn: '',
      birthdate: null,
      languages: [],
      street: '',
      city: '',
      state: '',
      zip: '',
      caseload: null,
      bio: '',
      headshotFile: null,
      driverLicenseFile: null,
    },
    validate: zod4Resolver(personalInfoSchema),
  });

  useEffect(() => {
    if (!profile || profile.resourceType !== 'Practitioner') {
      return;
    }

    const hydrationKey = `${profile.id ?? 'unknown'}:${profile.meta?.versionId ?? profile.meta?.lastUpdated ?? 'initial'}`;
    if (hydratedProfileVersionRef.current === hydrationKey) {
      return;
    }
    hydratedProfileVersionRef.current = hydrationKey;

    const officialName = getNameByUse(profile, 'official');
    const legalName = resolvePractitionerLegalName(profile);
    const preferredName = resolvePractitionerPreferredNamesForForm(profile);
    const aliasName = getNameByUse(profile, 'anonymous');
    const address = profile.address?.[0];

    form.setValues({
      businessPhone: formatPhoneNumber(
        profile.telecom?.find((entry) => entry.system === 'phone' && entry.use === 'work')?.value ?? '',
      ),
      firstName: officialName?.given?.[0] ?? legalName.given,
      lastName: officialName?.family ?? legalName.family,
      preferredFirstName: preferredName.preferredFirstName,
      preferredLastName: preferredName.preferredLastName,
      aliasFirst: aliasName?.given?.[0] ?? '',
      aliasMiddle: aliasName?.given?.[1] ?? '',
      aliasLast: aliasName?.family ?? '',
      npi: digitsOnly(getIdentifierValue(profile, NPI_IDENTIFIER_SYSTEM)).slice(0, 10),
      nuccTaxonomyCode: getNuccTaxonomyCode(profile) ?? null,
      ssn: digitsOnly(getIdentifierValue(profile, SSN_IDENTIFIER_SYSTEM)).slice(0, 9),
      birthdate: profile.birthDate ?? null,
      languages: (profile.communication ?? [])
        .map((concept) => concept.text ?? concept.coding?.[0]?.display ?? concept.coding?.[0]?.code ?? '')
        .filter(Boolean),
      street: address?.line?.[0] ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      zip: digitsOnly(address?.postalCode ?? '').slice(0, 5),
      caseload: getCaseloadPreference(profile) ?? null,
      bio: getBio(profile) ?? '',
      headshotFile: null,
      driverLicenseFile: null,
    });
  }, [profile]);

  const handleSave = form.onSubmit(
    async (values) => {
      if (!profile || profile.resourceType !== 'Practitioner' || !profile.id) {
        notifications.show({
          color: 'red',
          title: 'Could not save personal information',
          message: 'Provider profile is not loaded yet. Please try again.',
        });
        return;
      }

      setSaving(true);
      try {
        const existingDriverLicenseDocs = await medplum.searchResources('DocumentReference', {
          author: `Practitioner/${profile.id}`,
          type: `${PROVIDER_DOCUMENT_TYPE_SYSTEM}|drivers-license`,
          _count: '1',
        });

        const submissionValidation = personalInfoSaveSchema.safeParse({
          ...values,
          hasExistingHeadshot: Boolean(profile.photo?.[0]),
          hasExistingDriverLicense: existingDriverLicenseDocs.length > 0,
        });

        if (!submissionValidation.success) {
          const missingKeys: string[] = [];
          for (const issue of submissionValidation.error.issues) {
            const field = issue.path[0];
            if (typeof field === 'string') {
              form.setFieldError(field as keyof PersonalInfoFormValues, issue.message);
              missingKeys.push(field);
            }
          }
          notifications.show({
            color: 'red',
            title: 'Missing required fields',
            message: missingFieldMessage(missingKeys, personalInfoFieldLabels),
          });
          return;
        }

        let updated = upsertPractitionerOfficialName(profile, {
          given: [values.firstName.trim()],
          family: values.lastName.trim(),
        });

        const preferredFirstName = values.preferredFirstName.trim();
        const preferredLastName = values.preferredLastName.trim();
        updated = upsertPractitionerNickname(updated, {
          first: preferredFirstName,
          last: preferredLastName,
        });
        updated = stripPractitionerLegacyPreferredNames(updated);

        const aliasParts = [values.aliasFirst.trim(), values.aliasMiddle.trim()].filter(Boolean);
        updated = upsertName(
          updated,
          'anonymous',
          aliasParts.length > 0 || values.aliasLast.trim()
            ? {
              use: 'anonymous',
              given: aliasParts.length > 0 ? aliasParts : undefined,
              family: values.aliasLast.trim() || undefined,
            }
            : undefined,
        );

        updated = upsertTelecom(updated, {
          system: 'phone',
          use: 'work',
          value: values.businessPhone,
        });

        updated = upsertIdentifier(updated, {
          system: NPI_IDENTIFIER_SYSTEM,
          use: 'official',
          value: values.npi.trim(),
        });
        updated = upsertIdentifier(updated, {
          system: SSN_IDENTIFIER_SYSTEM,
          use: 'official',
          value: values.ssn.trim(),
        });

        const nuccLabel = NUCC_TAXONOMY_OPTIONS.find((option) => option.value === values.nuccTaxonomyCode)?.label;
        const nuccDisplay = nuccLabel?.split(' — ')[1];
        updated = upsertNuccTaxonomyQualification(updated, values.nuccTaxonomyCode ?? '', nuccDisplay);

        updated.birthDate = values.birthdate ?? undefined;
        updated.communication = buildLanguageConcepts(values.languages);
        updated.address = [
          {
            use: 'home',
            line: [values.street.trim()],
            city: values.city.trim(),
            state: values.state.trim(),
            postalCode: values.zip.trim(),
          } satisfies Address,
        ];

        updated = values.bio.trim() ? setBio(updated, values.bio.trim()) : removeExtension(updated, BIO_EXT);
        updated = values.caseload ? setCaseloadPreference(updated, values.caseload) : removeExtension(updated, CASELOAD_PREFERENCE_EXT);

        if (values.headshotFile) {
          const uploadedHeadshot = await createSecuredAttachment(
            medplum,
            values.headshotFile,
            { reference: `Practitioner/${updated.id}` },
            values.headshotFile.name,
            values.headshotFile.type,
          );
          updated.photo = [buildHeadshotAttachment(values.headshotFile, uploadedHeadshot)];
        }

        const savedPractitioner = await medplum.updateResource(updated);

        if (values.driverLicenseFile) {
          const attachment = await createSecuredAttachment(
            medplum,
            values.driverLicenseFile,
            { reference: `Practitioner/${savedPractitioner.id}` },
            values.driverLicenseFile.name,
            values.driverLicenseFile.type,
          );

          const existingDriverLicense = existingDriverLicenseDocs[0];
          if (existingDriverLicense) {
            await medplum.updateResource<DocumentReference>({
              ...existingDriverLicense,
              status: existingDriverLicense.status ?? 'current',
              date: new Date().toISOString(),
              author: [practitionerPreferredReference(savedPractitioner)],
              subject: practitionerPreferredReference(savedPractitioner),
              content: [{ attachment }],
            });
          } else {
            await medplum.createResource<DocumentReference>({
              resourceType: 'DocumentReference',
              status: 'current',
              date: new Date().toISOString(),
              author: [practitionerPreferredReference(savedPractitioner)],
              subject: practitionerPreferredReference(savedPractitioner),
              type: {
                coding: [
                  {
                    system: PROVIDER_DOCUMENT_TYPE_SYSTEM,
                    code: 'drivers-license',
                    display: "Driver's License",
                  },
                ],
              },
              content: [{ attachment }],
            });
          }
        }

        const sectionResult = await completeProviderOnboardingSection(medplum, 'personal-info');
        if (!sectionResult.ok) {
          const detail = sectionResult.missingSections
            .map((s) => `${s.title}: ${s.missingFields.join(', ')}`)
            .join('; ');
          notifications.show({
            color: 'red',
            title: 'Could not verify onboarding',
            message: detail || sectionResult.message,
          });
          return;
        }

        medplum.invalidateSearches('Practitioner');
        medplum.invalidateSearches('DocumentReference');
        medplum.invalidateSearches('Task');
        notifications.show({
          color: 'green',
          title: 'Personal information saved',
          message: 'Your information has been saved successfully.',
        });
        navigate('/onboarding');
      } catch (error) {
        notifications.show({
          color: 'red',
          title: 'Could not save personal information',
          message: error instanceof Error ? error.message : 'Please review required fields and try again.',
        });
      } finally {
        setSaving(false);
      }
    },
    () => {
      notifications.show({
        color: 'red',
        title: 'Missing required fields',
        message: missingFieldMessage(Object.keys(form.errors), personalInfoFieldLabels),
      });
    },
  );

  return (
    <OnboardingSection
      title="Personal Information"
      onSave={() => handleSave()}
      saveLabel="Save"
      isSaving={saving}
    >
      <Stack gap="md">
        <TextInput
          label="Phone number"
          placeholder="(555) 555-5555"
          value={form.values.businessPhone}
          onChange={(event) => form.setFieldValue('businessPhone', formatPhoneNumber(event.currentTarget.value))}
          error={form.errors.businessPhone}
          inputMode="tel"
          withAsterisk
        />
        <Group grow>
          <TextInput label="Legal first name" {...form.getInputProps('firstName')} withAsterisk />
          <TextInput label="Legal last name" {...form.getInputProps('lastName')} withAsterisk />
        </Group>
        <Group grow>
          <TextInput
            label="Preferred first name"
            placeholder="Preferred first name"
            {...form.getInputProps('preferredFirstName')}
          />
          <TextInput
            label="Preferred last name"
            placeholder="Preferred last name"
            {...form.getInputProps('preferredLastName')}
          />
        </Group>
        <Group grow>
          <TextInput label="Alias first name" placeholder="e.g. maiden name" {...form.getInputProps('aliasFirst')} />
          <TextInput label="Alias middle name" {...form.getInputProps('aliasMiddle')} />
          <TextInput label="Alias last name" {...form.getInputProps('aliasLast')} />
        </Group>
        <TextInput
          label="NPI number"
          placeholder="NPI"
          value={form.values.npi}
          onChange={(event) => form.setFieldValue('npi', digitsOnly(event.currentTarget.value).slice(0, 10))}
          error={form.errors.npi}
          inputMode="numeric"
          withAsterisk
        />
        <Select
          label="NUCC taxonomy code"
          placeholder="Select taxonomy code"
          description="Required for insurance claim submission as the rendering provider taxonomy."
          data={NUCC_TAXONOMY_OPTIONS}
          value={form.values.nuccTaxonomyCode}
          onChange={(value) => form.setFieldValue('nuccTaxonomyCode', value)}
          error={form.errors.nuccTaxonomyCode}
          searchable
          withAsterisk
        />
        <PasswordInput
          label="Social Security Number"
          placeholder="Social Security Number"
          value={form.values.ssn}
          onChange={(event) => form.setFieldValue('ssn', digitsOnly(event.currentTarget.value).slice(0, 9))}
          error={form.errors.ssn}
          inputMode="numeric"
          description="Encrypted and stored securely."
          withAsterisk
        />
        <UnifyDateInput
          label="Date of birth"
          placeholder="Select date"
          value={form.values.birthdate}
          onChange={(value) => form.setFieldValue('birthdate', value)}
          leftSection={<IconCalendarEvent size={16} />}
          withAsterisk
          error={form.errors.birthdate}
        />
        <ProfileCheckboxSection
          label="Languages spoken"
          options={LANGUAGES_OPTIONS}
          columns={3}
          value={form.values.languages}
          onChange={(value) => form.setFieldValue('languages', value)}
          withAsterisk
          error={typeof form.errors.languages === 'string' ? form.errors.languages : undefined}
        />
        <TextInput
          label="Street address"
          placeholder="Street address"
          {...form.getInputProps('street')}
          withAsterisk
        />
        <Group grow>
          <TextInput label="City" {...form.getInputProps('city')} withAsterisk />
          <Select
            label="State"
            placeholder="Select state"
            data={US_STATES}
            value={form.values.state || null}
            onChange={(value) => form.setFieldValue('state', value ?? '')}
            searchable
            withAsterisk
            error={form.errors.state}
          />
          <TextInput
            label="ZIP"
            placeholder="ZIP"
            value={form.values.zip}
            onChange={(event) => form.setFieldValue('zip', digitsOnly(event.currentTarget.value).slice(0, 5))}
            error={form.errors.zip}
            inputMode="numeric"
            withAsterisk
          />
        </Group>

        <Select
          label="Expected sessions per week"
          placeholder="Select a range"
          description="Set once during onboarding. Not editable after submission."
          data={CASELOAD_PREFERENCE_OPTIONS}
          value={form.values.caseload}
          onChange={(value) => form.setFieldValue('caseload', value)}
          withAsterisk
          error={form.errors.caseload}
        />
        <Textarea
          label="Profile bio"
          placeholder="Tell prospective clients about your background and approach."
          rows={7}
          withAsterisk
          {...form.getInputProps('bio')}
        />

        <DropzoneField
          label="Professional headshot"
          hint="A photo of yourself for your profile."
          accept="image/*"
          acceptLabel="PNG, JPG"
          withAsterisk
          value={form.values.headshotFile}
          onChange={(file) => form.setFieldValue('headshotFile', file)}
        />
        {form.errors.headshotFile && <Text size="xs" c="red">{form.errors.headshotFile}</Text>}

        <DropzoneField
          label="Driver's license"
          hint="Upload an image or PDF of your driver's license."
          accept="image/*,application/pdf"
          acceptLabel="PDF, PNG, JPG"
          withAsterisk
          value={form.values.driverLicenseFile}
          onChange={(file) => form.setFieldValue('driverLicenseFile', file)}
        />
        {form.errors.driverLicenseFile && <Text size="xs" c="red">{form.errors.driverLicenseFile}</Text>}
      </Stack>
    </OnboardingSection>
  );
}
